import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { sessionService } from '@/services/sessionService';
import type { SessionTemplate } from '@arrowise/shared';
import { useAuth } from '@/providers/AuthProvider';
import { SessionTemplateItem } from './SessionTemplateItem';
import { ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';

export default function SessionTemplatesPage({ nav = true, footer = true }: { nav?: boolean; footer?: boolean }) {
    const [templates, setTemplates] = useState<SessionTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const { teams } = useAuth();
    const [searchQuery, setSearchQuery] = useState<string>('');

    const filteredTemplates = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter((t) => {
            const hay = [t.name, t.description || '', t.language || '', t.difficulty || '']
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [templates, searchQuery]);

    useEffect(() => {
        // reset search when templates list changes (e.g. team switch)
        setSearchQuery('');
    }, [templates.length]);

    useEffect(() => {
        (async () => {
            try {
                let teamId: string | null = null;
                if (teams && teams.length > 0) {
                    teamId = teams[0].team.id;
                }
                const data = await sessionService.getAllTemplates(teamId);
                setTemplates(data);
            } catch (err: any) {
                toast.error(err?.message || 'Failed to load templates');
            } finally {
                setLoading(false);
            }
        })();
    }, [teams]);

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-5xl w-full space-y-8">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Button variant="outline" size="sm" asChild>
                            <Link to="/app/sessions">
                                <ArrowLeft />
                                Back
                            </Link>
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">Session Templates</h1>
                            <p className="text-sm text-muted-foreground mt-1">Browse all available interview templates for your account and teams.</p>
                        </div>
                    </div>
                </div>
                <div className="mb-8 w-1/2">
                    <Input
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="templates-search"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="h-64" />
                        ))
                    ) : templates.length === 0 ? (
                        <div className="col-span-full text-center text-muted-foreground py-12">No templates found.</div>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="col-span-full text-center text-muted-foreground py-12">No templates match your search.</div>
                    ) : (
                        filteredTemplates.map(template => (
                            <SessionTemplateItem key={template.id} template={template} />
                        ))
                    )}
                </div>
            </div>
        </PageLayout>
    );
}
